import { getDayjsLocaleByLanguage } from '@app/i18n/helpers';
import { useAppLanguage } from '@app/i18n/useAppLanguage';
import dayjs from '@app/utils/dayjs-setup';
import { Typography } from 'antd';
import React, { memo } from 'react';

interface DateTimeDisplayProps {
  /** Start date/time */
  startTime?: string | Date;
  /** End date/time (optional) */
  endTime?: string | Date;
  /** Show date on first line, time on second line */
  showDateSeparately?: boolean;
  /** Custom date format */
  dateFormat?: string;
  /** Custom time format */
  timeFormat?: string;
  /** Custom className for styling */
  className?: string;
  /** Custom className for date line */
  dateClassName?: string;
  /** Custom className for time line */
  timeClassName?: string;
  /** Render styled as link (blue, underline, pointer) */
  isLink?: boolean;
}

/**
 * DateTimeDisplay Component
 *
 * A reusable component for displaying date and time information with:
 * - Separate date and time lines
 * - Start and end time ranges
 * - Dayjs integration for consistent formatting
 * - Customizable formats and styling
 * - Memoized for performance
 *
 * @example
 * ```tsx
 * <DateTimeDisplay
 *   startTime={visit.startTime}
 *   endTime={visit.endTime}
 *   showDateSeparately
 * />
 * ```
 */
const DateTimeDisplayComponent: React.FC<DateTimeDisplayProps> = ({
  startTime,
  endTime,
  showDateSeparately = true,
  dateFormat = 'MMM DD, YYYY',
  timeFormat = 'HH:mm',
  className = '',
  dateClassName = '',
  timeClassName = '',
  isLink = false,
}) => {
  // Formatting is pinned to the active language rather than left to the global dayjs
  // locale. Reading the language through a subscription is what makes this memoized
  // component re-render on a language switch — its props don't change, so memo would
  // otherwise keep showing month/weekday names in the previously active language until
  // the page was reloaded.
  const language = useAppLanguage();
  const dayjsLocale = getDayjsLocaleByLanguage(language);

  // Return dash if no start time
  const linkClasses = isLink ? 'hover:underline cursor-pointer' : '';
  const linkStyle = isLink ? { color: '#00b3ff' } : undefined;

  if (!startTime) {
    return (
      <Typography.Text className={`${className} ${linkClasses}`.trim()} style={linkStyle}>
        -
      </Typography.Text>
    );
  }

  const startDayjs = dayjs(startTime).locale(dayjsLocale);
  const endDayjs = endTime ? dayjs(endTime).locale(dayjsLocale) : null;

  // Format date and time
  const dateText = startDayjs.format(dateFormat);
  const startTimeText = startDayjs.format(timeFormat || '');
  const endTimeText = endDayjs ? endDayjs.format(timeFormat || '') : null;

  // Build time range text
  const timeRangeText = endTimeText ? `${startTimeText} - ${endTimeText}` : startTimeText;

  const combinedDateClass = `block ${dateClassName} ${linkClasses}`.trim();
  const combinedTimeClass = `block ${timeClassName} ${linkClasses}`.trim();

  if (showDateSeparately) {
    return (
      <div className={className}>
        <Typography.Text className={combinedDateClass} style={linkStyle}>
          {dateText}
        </Typography.Text>
        <Typography.Text className={combinedTimeClass} style={linkStyle}>
          {timeRangeText}
        </Typography.Text>
      </div>
    );
  }

  // Single line format
  return (
    <Typography.Text className={`${className} ${linkClasses}`.trim()} style={linkStyle}>
      {dateText} {timeFormat && timeRangeText}
    </Typography.Text>
  );
};

// Memoized component with display name
DateTimeDisplayComponent.displayName = 'DateTimeDisplay';
export const DateTimeDisplay = memo(DateTimeDisplayComponent);

export default DateTimeDisplay;
